import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import { createStyles, makeStyles } from '@mui/styles';
import React, { useCallback, useState } from 'react';

const useStyles = makeStyles(() =>
  createStyles({
    table: {
      width: '100%',
    },
    tableCell: {
      padding: '8px !important',
      overflow: 'hidden',
      minWidth: '50px',
      maxWidth: '180px',
      whiteSpace: 'nowrap',
      fontSize: '14px !important',
    },
    tableContainer: {
      border: '1px solid #e0e0e0',
      borderRadius: '10px',
    },
    tableBody: {
      backgroundColor: '#ffffff',
    },
    expandableRow: {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#f5f5f5',
      },
    },
    expandedRowBg: {
      backgroundColor: '#f5f5f5',
    },
    skeletonCell: {
      padding: '8px !important',
      verticalAlign: 'middle',
    },
  }),
);

export interface ExpandableTableRow {
  id: string | number;
  cells: React.ReactNode[];
  expandedContent?: React.ReactNode;
}

export interface ExpandableTableProps {
  columns: string[];
  rows: ExpandableTableRow[];
  loading?: boolean;
  skeletonRowCount?: number;
}

const DEFAULT_SKELETON_ROW_COUNT = 5;

const ExpandableTable: React.FC<ExpandableTableProps> = ({
  columns,
  rows,
  loading = false,
  skeletonRowCount = DEFAULT_SKELETON_ROW_COUNT,
}) => {
  const classes = useStyles();
  const [expandedIds, setExpandedIds] = useState<Set<string | number>>(
    new Set(),
  );

  const toggleRow = useCallback((id: string | number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  return (
    <TableContainer className={classes.tableContainer}>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            {columns.map((col, idx) => (
              <TableCell key={idx}>{col}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody className={classes.tableBody}>
          {loading
            ? Array.from({ length: skeletonRowCount }).map((_, rowIdx) => (
                <TableRow key={`skeleton-${rowIdx}`}>
                  {columns.map((_, colIdx) => (
                    <TableCell
                      key={colIdx}
                      className={classes.skeletonCell}
                      style={{ borderBottom: '1px solid #e0e0e0' }}
                    >
                      <Skeleton
                        variant="text"
                        sx={{ fontSize: '1rem' }}
                        animation="wave"
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : rows.map((row) => {
                const isExpanded = expandedIds.has(row.id);
                return (
                  <React.Fragment key={row.id}>
                    <TableRow
                      onClick={() => toggleRow(row.id)}
                      className={`${classes.expandableRow} ${
                        isExpanded ? classes.expandedRowBg : ''
                      }`}
                    >
                      {row.cells.map((cell, cellIdx) => (
                        <TableCell key={cellIdx} className={classes.tableCell}>
                          {cell}
                        </TableCell>
                      ))}
                    </TableRow>
                    {isExpanded && row.expandedContent != null && (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          style={{ padding: 0 }}
                        >
                          {row.expandedContent}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ExpandableTable;
